import React, { useMemo } from "react";
import { Flag } from "./Flag";
import { Result } from "../../../../models/result.model";
import { StartListEntry } from "../../../../models/startlist.model";
import { Test } from "../../../../models/test.model";
import { markToDouble } from "../../../../tools";
import { useIsBreeding } from "../hooks/useIsBreeding";
import { useBackgroundColor } from "../hooks/useBackgroundColor";
import { useTest } from "../../../../contexts/competition.context";
import { useTextColor } from "../hooks/useTextColor";
import { Person } from "../../../../models/person.model";

const LIGHT_BACKGROUNDS = ["white", "yellow"];

export type ColumnProps = React.PropsWithChildren<
  {
    isLast?: boolean;
  } & React.HTMLAttributes<HTMLDivElement>
>;

export const Column: React.FC<ColumnProps> = ({
  isLast,
  children,
  ...rest
}) => {
  return (
    <div data-last={isLast} {...rest}>
      {children}
    </div>
  );
};

export type NameColumnProps = ColumnProps & {
  entry?: Result | StartListEntry;
  test: Test;
};

export const PrimaryNameColumn: React.FC<NameColumnProps> = ({
  entry,
  test,
  ...rest
}) => {
  const isBreeding = useIsBreeding(test);

  const name = () => {
    if (isBreeding) {
      return entry?.horse?.horseName;
    }

    let text = entry?.rider?.fullname;

    if (entry?.rider?.ageGroup) {
      text += ` [${entry?.rider?.ageGroup}]`;
    }

    return text;
  };

  return <Column {...rest}>{name()}</Column>;
};

export const SecondaryNameColumn: React.FC<
  NameColumnProps & { showFeifId?: boolean }
> = ({ entry, test, showFeifId = false, ...rest }) => {
  const isBreeding = useIsBreeding(test);

  const name = () => {
    if (!isBreeding) {
      return (
        <>
          <span>{entry?.horse?.horseName}</span>
          {showFeifId && (
            <span className="light ml-4">{entry?.horse?.feifId}</span>
          )}
        </>
      );
    }

    return (
      <>
        <span>{entry?.rider?.fullname}</span>
        {showFeifId && (
          <span className="light ml-4">{entry?.horse?.feifId}</span>
        )}
      </>
    );
  };

  return <Column {...rest}>{name()}</Column>;
};

export const ScopeColumn: React.FC<ColumnProps & { scope?: string | null }> = ({
  isLast,
  scope,
}) => {
  return (
    <Column
      isLast={isLast}
      style={{
        backgroundColor:
          scope === "AFIN"
            ? "var(--green)"
            : scope === "BFIN"
              ? "var(--yellow)"
              : "none",
        color: (scope === "BFIN" && "var(--black)") || "var(--white)",
      }}
    >
      {scope?.charAt(0)}
    </Column>
  );
};

type TeamColumnProps = ColumnProps & {
  rider?: Person;
  showCountryCode?: boolean;
};

export const TeamColumn: React.FC<TeamColumnProps> = ({
  rider,
  showCountryCode = true,
  ...rest
}) => {
  const countryCode = (() => {
    if (!rider) {
      return "";
    }

    if (rider.sportnationality && rider?.sportnationality !== "") {
      return rider.sportnationality;
    }

    if (rider.team && rider?.team !== "") {
      return rider.team;
    }

    return "";
  })();

  return (
    <Column className="team" style={{ flexShrink: 0, ...rest }} {...rest}>
      <Flag countryCode={countryCode} />
      {showCountryCode && <div>{countryCode}</div>}
    </Column>
  );
};

export const RankColumn: React.FC<
  ColumnProps & { result: Result; phase?: string }
> = ({ result, phase, ...rest }) => {
  const backgroundColor = useBackgroundColor(phase, result.color);
  const textColor = useTextColor(backgroundColor);

  return (
    <Column
      {...rest}
      className="center"
      style={{
        backgroundColor: `var(--${backgroundColor})`,
        color: textColor,
        textAlign: "center",
      }}
    >
      {result.state === "VALID" ? result.rank : "-"}
    </Column>
  );
};

export const StartGroupColumn: React.FC<
  ColumnProps & { startListEntry: StartListEntry; phase?: string }
> = ({ startListEntry, phase, ...rest }) => {
  const backgroundColor = useBackgroundColor(phase, startListEntry.color);
  const textColor = useTextColor(backgroundColor);

  return (
    <Column
      {...rest}
      className="center"
      style={{
        backgroundColor: `var(--${backgroundColor})`,
        color: textColor,
        textAlign: "center",
      }}
    >
      {startListEntry.startGroup}
    </Column>
  );
};

export type ResultColumnProps = {
  result: Result;
  test: Test;
} & ColumnProps;

export const ResultColumn: React.FC<ResultColumnProps> = ({
  result,
  test,
  ...rest
}) => {
  if (result.state) {
    return (
      <Column {...rest}>
        {result.state === "VALID"
          ? markToDouble(result.mark, test.roundingPrecision ?? 2)
          : result.state}
      </Column>
    );
  }

  return (
    <Column {...rest}>
      {markToDouble(result.mark, test.roundingPrecision ?? 2)}
    </Column>
  );
};
